/**
 * PatientHepGamification — Iter 75 patient-facing dashboard.
 *
 * Shows the streak counter, weekly goal progress, badges, and a "miles
 * improved" report (pain delta + ROM delta vs initial assessment).
 *
 * Mounted at /patient/progress.
 */
import React, { useEffect, useState } from 'react';
import { api, formatApiError } from '../../lib/api';
import { Flame, Trophy, Crown, Target, Heart, TrendingDown, CheckCircle2, Activity, Calendar, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BADGE_ICON = { flame: Flame, trophy: Trophy, crown: Crown, target: Target, heart: Heart, 'trending-down': TrendingDown };

export default function PatientHepGamification() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  // Iter 133 — Load the patient's currently-assigned HEP program so we can
  // POST /hep/adherence with valid program_id + video_id. Without these the
  // backend returns a 422 array-of-Pydantic-errors that used to crash the
  // toast render (see fix in formatApiError below).
  const [logTarget, setLogTarget] = useState(null); // { program_id, video_id, label }

  const reload = async () => {
    try {
      const { data } = await api.get('/hep/gamification/me');
      setData(data);
      // Celebrate when the weekly goal is met for the first time today.
      if (data?.weekly_done >= data?.weekly_goal && !localStorage.getItem(`hep_celebrated_${new Date().toISOString().slice(0, 10)}`)) {
        setCelebrate(true);
        localStorage.setItem(`hep_celebrated_${new Date().toISOString().slice(0, 10)}`, '1');
        setTimeout(() => setCelebrate(false), 4000);
      }
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    // Iter 133 — Also fetch the first active HEP program so the "Log today"
    // button has a valid target. If none exists, the button hides so the
    // user can't hit the 422 shape.
    api.get('/hep/programs/me')
      .then(r => {
        const active = (r.data || []).find(p => p.status === 'active') || (r.data || [])[0];
        if (active && (active.videos || []).length > 0) {
          setLogTarget({
            program_id: active.program_id,
            video_id: active.videos[0].video_id || active.videos[0].id,
            label: active.videos[0].title || 'today\'s session',
          });
        }
      })
      .catch(() => { /* no active program — hide button */ });
  }, []);

  const logToday = async () => {
    if (!logTarget) {
      toast.message('No active HEP program yet — your clinician will assign one.');
      return;
    }
    setLogging(true);
    try {
      await api.post('/hep/adherence', {
        program_id: logTarget.program_id,
        video_id: logTarget.video_id,
        difficulty: 5,
        pain_after: 3,
        done_at: new Date().toISOString(),
      });
      toast.success('Logged. Nice work!');
      await reload();
    } catch (e) {
      // Iter 133 — Guard against Pydantic-array error shape (which caused
      // "Objects are not valid as a React child" crashes when passed to
      // toast.error directly).
      toast.error(formatApiError(e?.response?.data?.detail) || 'Could not log session.');
    } finally { setLogging(false); }
  };

  if (loading) return <div className="p-8">Loading…</div>;
  if (!data) return <div className="p-8 text-fg-muted">Could not load your progress yet.</div>;

  const goalHit = data.weekly_done >= data.weekly_goal;
  const milesPain = data.miles_improved?.pain_delta;
  const milesRom = data.miles_improved?.rom_delta_deg;

  return (
    <div className="p-4 md:p-8 max-w-4xl relative" data-testid="hep-gamification">
      {celebrate && <CelebrationOverlay/>}

      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">MY PROGRESS</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="hep-title">
            Every session moves you forward.
          </h1>
          <p className="text-fg-muted text-sm mt-1">
            Hit your home-exercise goal every week and you'll see real change. We do the maths so you can see it.
          </p>
        </div>
        {logTarget && (
          <button onClick={logToday} disabled={logging}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="hep-log">
            {logging ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
            Log today&apos;s session
          </button>
        )}
      </div>

      {/* Streak + weekly progress */}
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="bento-card text-center" data-testid="hep-streak">
          <Flame className="w-10 h-10 text-[#FFB000] mx-auto mb-2"/>
          <div className="text-5xl font-heading font-bold">{data.streak_days}</div>
          <div className="text-xs text-fg-muted mt-1">day streak</div>
        </div>
        <div className="bento-card md:col-span-2" data-testid="hep-weekly">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <Target className="w-4 h-4 text-brand"/> Weekly goal
            </h3>
            <span className={`text-xs font-bold ${goalHit ? 'text-[#1B5E20]' : 'text-fg-muted'}`}>
              {data.weekly_done} / {data.weekly_goal} sessions
            </span>
          </div>
          <div className="h-3 rounded-full bg-bg-secondary overflow-hidden">
            <div className={`h-full transition-all ${goalHit ? 'bg-[#1B5E20]' : 'bg-brand'}`}
                 style={{ width: `${Math.min(100, data.weekly_pct)}%` }}/>
          </div>
          <div className="text-xs text-fg-muted mt-2">
            {goalHit
              ? "🎉 You hit your weekly goal. Keep the streak going!"
              : `${data.weekly_goal - data.weekly_done} more session${data.weekly_goal - data.weekly_done === 1 ? '' : 's'} to hit your weekly goal.`}
          </div>
        </div>
      </div>

      {/* Miles improved */}
      <div className="bento-card mb-4" data-testid="hep-miles">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-brand"/> Miles improved (since you started)
        </h3>
        <div className="grid md:grid-cols-3 gap-3">
          <MileTile icon={Heart} label="Pain reduced by"
                    value={milesPain != null ? `${milesPain >= 0 ? '-' : '+'}${Math.abs(milesPain)} pts` : '—'}
                    good={milesPain != null && milesPain > 0}/>
          <MileTile icon={Activity} label="Range of motion gained"
                    value={milesRom != null ? `${milesRom >= 0 ? '+' : ''}${milesRom}°` : '—'}
                    good={milesRom != null && milesRom > 0}/>
          <MileTile icon={Calendar} label="Weeks active"
                    value={`${data.miles_improved?.weeks_active || 0}`}
                    good={(data.miles_improved?.weeks_active || 0) > 0}/>
        </div>
        <div className="text-[11px] text-fg-muted mt-3">
          Total sessions logged: <strong>{data.miles_improved?.total_sessions || 0}</strong>.
          Showing your initial assessment vs your most recent.
        </div>
      </div>

      {/* Badges */}
      <div className="bento-card" data-testid="hep-badges">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand"/> Badges
        </h3>
        {data.badges.length === 0 ? (
          <p className="text-sm text-fg-muted">Hit your first 3-day streak to unlock your first badge.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="hep-badge-list">
            {data.badges.map((b) => {
              const Icon = BADGE_ICON[b.icon] || Sparkles;
              return (
                <div key={b.key}
                     className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#FFB000]/10 border border-[#FFB000]/30 text-[#003D80]"
                     data-testid={`hep-badge-${b.key}`}>
                  <Icon className="w-4 h-4 text-[#FFB000]"/>
                  <span className="text-xs font-semibold">{b.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MileTile({ icon: Icon, label, value, good }) {
  return (
    <div className={`rounded-lg border p-3 ${good ? 'bg-[#E8F5E9] border-[#1B5E20]/40' : 'border-soft bg-bg-secondary/30'}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-fg-muted">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className={`text-2xl font-heading font-bold mt-1 ${good ? 'text-[#1B5E20]' : 'text-fg'}`}>{value}</div>
    </div>
  );
}

function CelebrationOverlay() {
  // Small confetti-style flourish (CSS-only — no extra deps).
  return (
    <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center" data-testid="hep-celebrate">
      <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center animate-bounce">
        <Trophy className="w-12 h-12 text-[#FFB000] mx-auto mb-2"/>
        <h2 className="text-2xl font-heading font-bold">Weekly goal hit! 🎉</h2>
        <p className="text-sm text-fg-muted mt-1">Streak protected. Keep it rolling.</p>
      </div>
    </div>
  );
}
